/**
 * The conversation on screen, and how Orik asks for another one.
 *
 * `ui-workspace` owns navigation: its browser in the sidebar opens conversations and starts new ones, and it
 * restores the last conversation when the page loads. Orik's own components read which session is on screen
 * from the bridge (`harness/orik-client.ts`) and ask `ui-workspace` to navigate for the two things that start
 * outside it — a `?conversation=` link and the sidebar's "New conversation" button. Choosing a new
 * conversation's repository is the hero's own workspace picker.
 */
import { useSyncExternalStore } from "react";
import type { OrikDshBridge, Observable } from "../../../harness/orik-client";
import { dshBridge } from "./session-bridge";

/** Kept as literals so the app's entry does not import the lazy harness chunk. */
const BRIDGE_EVENT = "atlas:dsh-catalogue-ready";
const BRIDGE_GLOBAL = "__ORIK_DSH__";

/** Where `ui-workspace` persists the selection it restores at boot (`dsh.sessions.current`). */
const SELECTION_KEY = "dsh.sessions.current";

function published(): OrikDshBridge | undefined {
  return (globalThis as Record<string, unknown>)[BRIDGE_GLOBAL] as OrikDshBridge | undefined;
}

/** One bridge observable as an external store, across the kernel's asynchronous publication. */
function bridgeStore<T>(pick: (bridge: OrikDshBridge) => Observable<T>, fallback: T) {
  return {
    subscribe(listener: () => void): () => void {
      let stop: (() => void) | undefined;
      // The kernel's stores notify synchronously, sometimes while one of its own components renders (a retain
      // during render); React refuses a different component's update then, so the notification is deferred.
      let live = true;
      const notify = (): void => { queueMicrotask(() => { if (live) listener(); }); };
      const attach = (): void => {
        const bridge = published();
        if (bridge === undefined || stop !== undefined) return;
        stop = pick(bridge).subscribe(notify);
        notify();
      };
      window.addEventListener(BRIDGE_EVENT, attach);
      attach();
      return () => { live = false; window.removeEventListener(BRIDGE_EVENT, attach); stop?.(); };
    },
    getSnapshot(): T {
      const bridge = published();
      return bridge === undefined ? fallback : pick(bridge).getSnapshot();
    },
  };
}

const mainSession = bridgeStore(bridge => bridge.mainSession, undefined);

/** The session on screen in the conversation panel; undefined before boot and on the New Session page. */
export function useMainSession(): string | undefined {
  return useSyncExternalStore(mainSession.subscribe, mainSession.getSnapshot, () => undefined);
}

/**
 * Put a conversation on screen.
 *
 * Before the kernel boots, the conversation is written where `ui-workspace` restores its selection from, so the
 * first thing the page shows is this conversation rather than the last one.
 */
export async function openConversation(conversationId: string): Promise<void> {
  if (published() === undefined) {
    try { localStorage.setItem(SELECTION_KEY, JSON.stringify({ sessionId: conversationId })); } catch { /* blocked storage: the open below still applies */ }
  }
  const bridge = await dshBridge();
  if (bridge.mainSession.getSnapshot() !== conversationId) await bridge.openSession(conversationId);
}

/** `ui-workspace`'s New Session: a blank conversation in the current conversation's workspace, else the most recent one. */
export async function startConversation(): Promise<void> {
  (await dshBridge()).startSession();
}
